import React from 'react';
import { GeneratedImage } from '../types';

interface ImageModalProps {
    image: GeneratedImage;
    onClose: () => void;
    onDelete: (image: GeneratedImage) => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ image, onClose, onDelete }) => {
    const handleDownload = async () => {
        try {
            const response = await fetch(image.image_url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `quote-image-${image.id}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Download failed', error);
        }
    };

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-6 bg-dark-950/95 backdrop-blur-md animate-fade-in">
            {/* Backdrop click to close */}
            <div className="absolute inset-0" onClick={onClose} />

            <div className="relative w-full max-w-5xl bg-dark-900 rounded-3xl border border-white/5 shadow-2xl overflow-hidden animate-zoom-in flex flex-col md:flex-row min-h-[400px]">
                {/* Close Button (Mobile & Desktop) */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-20 p-2 rounded-full bg-dark-800/80 text-white hover:bg-dark-700 transition-all border border-white/10"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Left Side: Image Preview (The Hero) */}
                <div className="flex-[1.5] bg-[#050507] flex items-center justify-center p-6 sm:p-10 border-b md:border-b-0 md:border-r border-white/5">
                    <img
                        src={image.image_url}
                        alt={image.content}
                        className="max-w-full max-h-[60vh] md:max-h-[80vh] rounded-xl shadow-2xl object-contain transition-transform duration-500 hover:scale-[1.01]"
                    />
                </div>

                {/* Right Side: Info & Actions */}
                <div className="flex-1 p-6 sm:p-8 flex flex-col justify-between bg-dark-900/50">
                    <div className="space-y-6">
                        <div>
                            <span className="text-[10px] font-bold text-primary-400 uppercase tracking-[0.2em] mb-3 block">Quote Content</span>
                            <div className="p-4 rounded-2xl bg-white/5 border border-white/5">
                                <p className="text-white/90 text-sm leading-relaxed font-medium italic">"{image.content}"</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <span className="text-[10px] font-bold text-dark-500 uppercase tracking-[0.2em] mb-1 block">Quick Actions</span>
                            <button
                                onClick={handleDownload}
                                className="w-full btn-primary flex items-center justify-center gap-3 py-3.5 shadow-glow"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                <span className="font-bold">Download PNG</span>
                            </button>
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-white/5">
                        <button
                            onClick={() => onDelete(image)}
                            className="w-full group flex items-center justify-center gap-2 py-3 text-danger-500 hover:text-danger-400 transition-all rounded-xl hover:bg-danger-500/5"
                        >
                            <svg className="w-4 h-4 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span className="text-xs font-bold uppercase tracking-wider">Delete from Cloud</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageModal;
